"use client";

import { useCallback, useMemo } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { AutocompleteSearchBar } from "@/components/search/autocomplete-search-bar";
import { PageHeroWithSearch } from "@/components/sections/page-hero-with-search";
import { PageBackground } from "@/components/sections/page-background";
import { CategoryPills } from "@/components/category-pills";
import { ScrollTile } from "@/components/scroll-tile";
import { TileGrid, TileGridSkeleton } from "@/components/tile-grid";
import { SimplePagination } from "@/components/pagination/simple-pagination";
import { useDiscountCodes, useMainCategoriesWithCounts } from "@/hooks/queries";
import { DiscountCodeStatus } from "@/openapi/generated";

const CODES_PER_PAGE = 30;
const ALL_SENTINEL = "all";

export function DiscountCodesPageContent() {
  const t = useTranslations("discountCodes");
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const searchQuery = searchParams.get("search") ?? "";
  const selectedCategory = searchParams.get("category") ?? ALL_SENTINEL;
  const pageParam = searchParams.get("page");
  const currentPage = Math.max(1, pageParam ? parseInt(pageParam, 10) || 1 : 1);

  const buildUrl = useCallback(
    (overrides: { search?: string; category?: string; page?: number }) => {
      const params = new URLSearchParams();
      const search = overrides.search ?? searchQuery;
      const category = overrides.category ?? selectedCategory;
      if (search) params.set("search", search);
      if (category !== ALL_SENTINEL) params.set("category", category);
      if ((overrides.page ?? 1) > 1) params.set("page", String(overrides.page));
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [searchQuery, selectedCategory, pathname],
  );

  const handleSearch = useCallback(
    (value: string) => {
      router.replace(buildUrl({ search: value, page: 1 }), { scroll: false });
    },
    [buildUrl, router],
  );

  const handleCategorySelect = useCallback(
    (categoryId: string) => {
      router.replace(buildUrl({ category: categoryId, page: 1 }), { scroll: false });
    },
    [buildUrl, router],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      router.replace(buildUrl({ page }), { scroll: false });
    },
    [buildUrl, router],
  );

  const { data: categoriesData } = useMainCategoriesWithCounts();
  const categoryItems = useMemo(
    () => [
      { id: ALL_SENTINEL, label: t("page.allCategories"), icon: "tag" },
      ...(categoriesData?.categories ?? []).map((cat) => ({
        id: cat.id,
        label: cat.name,
        icon: cat.icon,
        color: cat.color,
      })),
    ],
    [categoriesData?.categories, t],
  );

  const { data, isLoading } = useDiscountCodes({
    page: currentPage - 1,
    size: CODES_PER_PAGE,
    search: searchQuery || undefined,
    status: DiscountCodeStatus.ACTIVE,
    categoryId: selectedCategory !== ALL_SENTINEL ? selectedCategory : undefined,
  });

  const codes = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <PageBackground>
      <PageHeroWithSearch
        title={t("page.hero.headline")}
        subtitle={t("page.hero.subtitle")}
      >
        <AutocompleteSearchBar
          key={searchQuery}
          sources={["discountCodes"]}
          onSubmit={handleSearch}
          defaultValue={searchQuery}
          placeholder={t("page.hero.searchPlaceholder")}
        />
      </PageHeroWithSearch>

      <section className="py-4">
        <div className="max-w-7xl mx-auto">
          <CategoryPills
            items={categoryItems}
            activeId={selectedCategory}
            onSelect={handleCategorySelect}
          />
        </div>
      </section>

      <div className="max-w-7xl mx-auto space-y-6 pb-8">
        {isLoading ? (
          <TileGridSkeleton count={12}  />
        ) : codes.length > 0 ? (
          <TileGrid >
            {codes.map((code) => (
              <ScrollTile
                key={code.id}
                href={code.shopSlug ? `/shops/${code.shopSlug}` : undefined}
                name={code.title ?? code.shopName ?? ""}
                logoUrl={code.shopLogoUrl}
                subtitle={
                  <p className="text-xs text-muted-foreground text-center truncate w-full">
                    {code.discountPercentage
                      ? `${code.discountPercentage}%`
                      : code.discountAmount
                        ? `${code.discountAmount} ${code.currencyCode ?? "€"}`
                        : ""}
                    {code.endsAt ? ` · ${t("validUntil", { date: new Date(code.endsAt).toLocaleDateString() })}` : ""}
                  </p>
                }
              />
            ))}
          </TileGrid>
        ) : (
          <div className="text-center py-10">
            <p className="text-muted-foreground">{t("page.noResults")}</p>
          </div>
        )}

        <SimplePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </PageBackground>
  );
}
